// src/pages/JournalRecord/components/exportJournalRecordCsv.js
import { formatToCompanyTimezone } from "../../../../lib/functions";

const fmt = (num) =>
  Number(num || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const escape = (val) => {
  const str = String(val ?? "");
  return str.includes(",") || str.includes('"') || str.includes("\n")
    ? `"${str.replace(/"/g, '""')}"`
    : str;
};

const formatDate = (v) =>
  v ? formatToCompanyTimezone(v).split(" ")[0] : "-";

const triggerDownload = (rows, filename) => {
  const csv  = rows.map((r) => r.join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * @param {object} params
 * @param {Array}   params.list     - from Redux state.journalRecord.list (already mapped)
 * @param {object}  params.filters  - from Redux state.journalRecord.filters
 * @param {string}  params.title
 * @param {string}  params.filename
 */
export const exportJournalRecordCsv = ({
  list,
  filters,
  title    = "Journal Record Report",
  filename = "journal_record",
}) => {

  // ── By Account ─────────────────────────────────────────────────────────────
  if (filters.filterType === "By Account") {
    const headers = [
      "No.", "VCH No", "Date", "Receipt By", "Account",
      "Debit Total", "Credit Total",
    ].map(escape);

    const dataRows = list.map((row, i) => [
      i + 1,
      row.jrnCode   || "",
      formatDate(row.date),
      row.journalBy || "",
      row.account   || "",
      fmt(row.debitTotal),
      fmt(row.creditTotal),
    ].map(escape));

    const grandDebit  = list.reduce((s, r) => s + Number(r.debitTotal  || 0), 0);
    const grandCredit = list.reduce((s, r) => s + Number(r.creditTotal || 0), 0);

    const totalRow = [
      "Grand Total:", "", "", "", "", fmt(grandDebit), fmt(grandCredit),
    ].map(escape);

    return triggerDownload([[title], [], headers, ...dataRows, [], totalRow], filename);
  }

  // ── With Details ───────────────────────────────────────────────────────────
  if (filters.recordType === "with details") {
    const headers = [
      "No.", "VCH No", "Date", "Journal By", "Account",
      "Debit Amount", "Credit Amount",
    ].map(escape);

    const dataRows = [];
    let grandDebit  = 0;
    let grandCredit = 0;

    list.forEach((row, i) => {
      const details = row.details || [];
      const firstAcc = details.length > 0 ? details[0].acc_name : "-";

      // parent row (mirrors parentColumns — Account = first detail's acc_name)
      dataRows.push([
        i + 1,
        row.jrnCode   || "",
        formatDate(row.date),
        row.journalBy || "",
        firstAcc,
        fmt(row.debitTotal),
        fmt(row.creditTotal),
      ].map(escape));

      // child detail rows
      let subDebit  = 0;
      let subCredit = 0;
      details.forEach((d, di) => {
        subDebit  += Number(d.debit_amount  || 0);
        subCredit += Number(d.credit_amount || 0);
        dataRows.push([
          "",
          `  └ ${di + 1}`,
          "",
          "",
          d.acc_name || "",
          fmt(d.debit_amount),
          fmt(d.credit_amount),
        ].map(escape));
      });

      // sub-total row
      dataRows.push([
        "", "", "", "", "Sub Total:", fmt(subDebit), fmt(subCredit),
      ].map(escape));
      dataRows.push([]); // blank separator

      grandDebit  += subDebit;
      grandCredit += subCredit;
    });

    const totalRow = [
      "Grand Total:", "", "", "", "", fmt(grandDebit), fmt(grandCredit),
    ].map(escape);

    return triggerDownload([[title], [], headers, ...dataRows, totalRow], filename);
  }

  // ── Without Details (default) ──────────────────────────────────────────────
  const headers = [
    "No.", "VCH No", "Date", "Journal By",
    "Debit Total", "Credit Total",
  ].map(escape);

  const dataRows = list.map((row, i) => [
    i + 1,
    row.jrnCode   || "",
    formatDate(row.date),
    row.journalBy || "",
    fmt(row.debitTotal),
    fmt(row.creditTotal),
  ].map(escape));

  const grandDebit  = list.reduce((s, r) => s + Number(r.debitTotal  || 0), 0);
  const grandCredit = list.reduce((s, r) => s + Number(r.creditTotal || 0), 0);

  const totalRow = [
    "Grand Total:", "", "", "", fmt(grandDebit), fmt(grandCredit),
  ].map(escape);

  triggerDownload([[title], [], headers, ...dataRows, [], totalRow], filename);
};